import { computed, shallowRef, unref } from 'vue-demi';
import useItemLoader from '../base/useItemLoader';
import { useRealTimeUpdates } from '../base/useRealTimeUpdates';
import useRefreshCount from '../base/useRefreshCount';

function responseToItem(response) {
  return response.data.meta.page.count;
}

/**
 * Loads the count of tasks matching the given criteria.
 *
 * @param {Object} options - Options for the loader.
 * @param {number|import('vue-demi').Ref<number>} [options.taskId] - The task from which to load subtasks.
 * @param {number|import('vue-demi').Ref<number>} [options.tasklistId] - The task list from which to load tasks.
 * @param {number|import('vue-demi').Ref<number>} [options.projectId] - The project from which to load tasks.
 * @param {Object} [options.params] - Filtering params for the request.
 * @param {boolean|import('vue-demi').Ref<boolean>} [options.active=true] - Determines if the loader should actually load the data.
 * @returns {Object}
 */
export default function useTaskCountV3Loader({
  taskId: _taskId,
  tasklistId: _tasklistId,
  projectId: _projectId,
  params: _params,
  active: _active = true,
} = {}) {
  const taskId = shallowRef(_taskId);
  const tasklistId = shallowRef(_tasklistId);
  const projectId = shallowRef(_projectId);
  const active = shallowRef(_active);

  const url = computed(() => {
    if (!active.value) {
      return null;
    }
    if (taskId.value) {
      return `/projects/api/v3/tasks/${taskId.value}/subtasks.json`;
    }
    if (tasklistId.value) {
      return `/projects/api/v3/tasklists/${tasklistId.value}/tasks.json`;
    }
    if (projectId.value) {
      return `/projects/api/v3/projects/${projectId.value}/tasks.json`;
    }
    return '/projects/api/v3/tasks.json';
  });

  const params = computed(() => ({
    ...unref(_params),
    page: undefined,
    pageSize: 1,
    cursor: undefined,
    limit: undefined,
    orderBy: undefined,
    orderMode: undefined,
    skipCounts: false,
    'fields[tasks]': 'id',
    include: undefined,
    includeRelatedTasks: undefined,
    includeCommentStats: undefined,
    includeCompanyUserIds: undefined,
  }));

  const { state, refresh } = useItemLoader({
    url,
    params,
    responseToItem,
  });

  const refreshCount = useRefreshCount(state, refresh);

  useRealTimeUpdates((event) => {
    // The event name is misleading, as task modifications are not limited to a single project.
    if (event.type === 'projectTasks') {
      // Force refresh because a lot of tasks might have changed.
      refresh();
      return;
    }

    if (event.type === 'task' && event.action === 'bulkEditTasks') {
      // Force refresh because a lot of tasks might have changed.
      refresh();
      return;
    }

    // Skip non-task events.
    if (event.type !== 'task') {
      return;
    }

    // If filtering by projectId, skip changes in other projects.
    if (projectId.value && projectId.value !== event.projectId && projectId.value !== event.oldProjectId) {
      return;
    }

    // If filtering by tasklistId, skip changes in other tasklists.
    if (tasklistId.value && tasklistId.value !== event.tasklistId && tasklistId.value !== event.oldTasklistId) {
      return;
    }

    // If loading only one level from the tasks hierarchy...
    if (!params.value.getSubTasks) {
      if (taskId.value) {
        // If loading subtasks, skip refreshing tasks having a different parent.
        if (taskId.value !== event.parentTaskId && taskId.value !== event.oldParentTaskId) {
          return;
        }
      } else if (event.parentTaskId > 0 && event.oldParentTaskId > 0) {
        // If loading main tasks, skip refreshing subtasks.
        return;
      }
    }

    refreshCount();
  });

  return state;
}
